import { addHours, format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useContext, useEffect, useRef, useState } from 'react'
import { IconButton, InputAdornment, TextField } from '@mui/material'

import './styles/index.css'
import { useSocket } from '../../hooks/use-socket'
import { requestAccessToken } from '../../api/auth.api'
import Pagination from '../../components/common/pagination'
import SearchIcon from '../../assets/icons/search-icon.svg'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { handleIsValidMatch } from '../../utils/common.utils'
import DownloadIcon from '../../assets/icons/download-icon.svg'
import { getFundingsDataByCustomerCompany } from '../../api/app-queries.api'
import { CustomerCompany, CustomerCompanyFunding, FormError } from '../../types'
import { handleSyncDataToLocalDB, normalizeCustomerCompanyFundingData } from '../../utils'
import CustomerCompanyFundingDataListItem from './customer-company-funding-data-list-item'
import { RevertCustomerCompanyFundingModal } from './revert-customer-company-funding-modal'
import { RejectCustomerCompanyFundingModal } from './reject-customer-company-funding-modal'
import { CustomerCompanyFundingDetailsModal } from './customer-company-funding-details-modal'
import { handleDeleteDataByOne, handleGetDataFromTableById } from '../../utils/db/common-methods'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../../contexts'
import { CustomerCompanyFundingsHistoryReportModal } from './customer-company-fundings-history-report-modal'

export const CustomerCompanyFundingsHistory = () => {
  const navigate = useNavigate()
  const { customerCompanyId } = useParams()
  const { t } = useTranslation(['customer-company-details-management', 'payments-to-review-management', 'customer-details-management'])

  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { setScrollToTop } = useContext(AppStateContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const currentListPageRef = useRef<number>(1)
  const currentLoadingDataRef = useRef<boolean>(true)
  const customerCompanyFundingToSearchRef = useRef<string>('')
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const currentCustomerCompanyFundingsDataRef = useRef<Array<CustomerCompanyFunding>>([])

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [customerCompanyFundingToSearch, setCustomerCompanyFundingToSearch] = useState<string>('')
  const [openFundingsHistoryReportModal, setOpenFundingsHistoryReportModal] = useState<boolean>(false)
  const [selectedCustomerCompanyFunding, setSelectedCustomerCompanyFunding] = useState<CustomerCompanyFunding>()
  const [customerCompanyFundingsList, setCustomerCompanyFundingsList] = useState<Array<CustomerCompanyFunding>>([])
  const [customerCompanyFundingsData, setCustomerCompanyFundingsData] = useState<Array<CustomerCompanyFunding>>([])
  const [openRevertCustomerCompanyFundingModal, setOpenRevertCustomerCompanyFundingModal] = useState<boolean>(false)
  const [openRejectCustomerCompanyFundingModal, setOpenRejectCustomerCompanyFundingModal] = useState<boolean>(false)
  const [openCustomerCompanyFundingDetailsModal, setOpenCustomerCompanyFundingDetailsModal] = useState<boolean>(false)
  const [searchedCustomerCompanyFundings, setSearchedCustomerCompanyFundings] = useState<Array<CustomerCompanyFunding>>([])

  useEffect(() => {
    handleGetFundingsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!appDataSocket) return

    appDataSocket.on('addNewCustomerCompanyFunding', async () => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      handleGetFundingsDataFromRemote(selectedCountry)
    })

    appDataSocket.on('updateCustomerCompanyFunding', async () => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      handleGetFundingsDataFromRemote(selectedCountry)
    })

    appDataSocket.on('deleteCustomerCompanyFunding', async (dataFromSocket: any) => {
      handleDeleteCustomerCompanyFundingFromLocalDB(dataFromSocket.documentKey)
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      handleGetFundingsDataFromRemote(selectedCountry)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const customerCompanyFundingsPerPage = customerCompanyFundingToSearch === '' ? customerCompanyFundingsData.slice(firstPageIndex, lastPageIndex) : searchedCustomerCompanyFundings.slice(firstPageIndex, lastPageIndex)

    setCustomerCompanyFundingsList(customerCompanyFundingsPerPage)

    for (const customerCompanyFundingPerPage of customerCompanyFundingsPerPage) {
      const customerCompanyFundingToAdd = normalizeCustomerCompanyFundingData(customerCompanyFundingPerPage)
      handleSyncDataToLocalDB('customer-companies', customerCompanyFundingToAdd)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, customerCompanyFundingsData, searchedCustomerCompanyFundings])

  const handleGetFundingsData = async () => {
    let localFundingsDataToSet: any = {}
    const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
    const localFundingsData = globalThis.sessionStorage.getItem(`fundingsData:${selectedCountry}:${customerCompanyId}`)

    if (localFundingsData) {
      localFundingsDataToSet = JSON.parse(localFundingsData)
      handleSetInitialFundingsData(localFundingsDataToSet)
    }

    handleGetFundingsDataFromRemote(selectedCountry)
  }

  const handleGetFundingsDataFromRemote = async (selectedCountry: string) => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getFundingsDataByCustomerCompany(String(customerCompanyId), selectedCountry, accessToken)

      globalThis.sessionStorage.setItem(`fundingsData:${selectedCountry}:${customerCompanyId}`, JSON.stringify(data))
      handleSetInitialFundingsData(data)
    } catch (error: any) {
      console.log('Error handleGetCustomerCompaniesBriefDataFromRemote > ', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleSetInitialFundingsData = (dataToSet: any) => {
    let customerCompanyFundingsToSet = null

    if (userSession?.role?.includes('-client')) {
      customerCompanyFundingsToSet = dataToSet.currentFundingsData.filter((fundingData: CustomerCompanyFunding) => fundingData.status === 'approved' || fundingData.status === 'pending-credit')
    } else {
      customerCompanyFundingsToSet = [...dataToSet.currentFundingsData]
    }

    if (customerCompanyFundingsToSet.length === 0 && currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      currentCustomerCompanyFundingsDataRef.current = []
      setLoadingLocalData(false)
      return
    }

    const currentListPageDataState = currentListPageRef.current
    const currentCustomerCompanyFundingToSearchState = customerCompanyFundingToSearchRef.current
    const customerCompanyFundingsNormalizedData = []

    for (const customerCompanyFunding of customerCompanyFundingsToSet) {
      const customerCompanyFundingData = { ...customerCompanyFunding }
      const customerCompanyFundingDataNormalized = normalizeCustomerCompanyFundingData(customerCompanyFundingData)
      customerCompanyFundingsNormalizedData.push(customerCompanyFundingDataNormalized)
    }

    customerCompanyFundingsNormalizedData.sort((thisCustomerCompanyFunding: Partial<CustomerCompanyFunding>, nextCustomerCompanyFunding: Partial<CustomerCompanyFunding>) => {
      return Date.parse(String(nextCustomerCompanyFunding.createdAt)) - Date.parse(String(thisCustomerCompanyFunding.createdAt))
    })

    if (currentCustomerCompanyFundingToSearchState === '' || currentCustomerCompanyFundingToSearchState === null) {
      const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const customerCompanyFundingsPerPage = customerCompanyFundingsNormalizedData.slice(firstPageIndex, lastPageIndex)

      setCustomerCompanyFundingsList(customerCompanyFundingsPerPage as Array<CustomerCompanyFunding>)

      for (const customerCompanyFundingPerPage of customerCompanyFundingsPerPage) {
        handleSyncDataToLocalDB('customer-companies', customerCompanyFundingPerPage)
      }
    }

    if (selectedCustomerCompanyFunding) {
      const customerCompanyFundingIndex = customerCompanyFundingsNormalizedData.findIndex((customerCompanyFunding) => String(customerCompanyFunding._id) === String(selectedCustomerCompanyFunding._id))

      if (
        customerCompanyFundingIndex > -1 &&
        customerCompanyFundingsNormalizedData[customerCompanyFundingIndex].status === 'rejected' &&
        String(userSession?._id) !== String(customerCompanyFundingsNormalizedData[customerCompanyFundingIndex].rejectedBy.systemUserId)
      ) {
        setOpenRevertCustomerCompanyFundingModal(false)
        setOpenRejectCustomerCompanyFundingModal(false)
        setOpenCustomerCompanyFundingDetailsModal(false)
        updateNewNotificationStatus?.(true, 'someone-else-resolve-allocation')
      }

      if (
        customerCompanyFundingIndex > -1 &&
        customerCompanyFundingsNormalizedData[customerCompanyFundingIndex].status === 'approved' &&
        String(userSession?._id) !== String(customerCompanyFundingsNormalizedData[customerCompanyFundingIndex].assignedBy.systemUserId)
      ) {
        setOpenRevertCustomerCompanyFundingModal(false)
        setOpenRejectCustomerCompanyFundingModal(false)
        setOpenCustomerCompanyFundingDetailsModal(false)
        updateNewNotificationStatus?.(true, 'someone-else-resolve-allocation')
      }
    }

    currentCustomerCompanyFundingsDataRef.current = customerCompanyFundingsNormalizedData
    setCustomerCompanyFundingsData(customerCompanyFundingsNormalizedData as Array<CustomerCompanyFunding>)

    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
    }
  }

  const handleDeleteCustomerCompanyFundingFromLocalDB = async (documentKey: string) => {
    const customerCompanyFundingDataSetted: Array<CustomerCompany> = (await handleGetDataFromTableById('customer-company-fundings', String(documentKey))) as Array<CustomerCompany>

    if (customerCompanyFundingDataSetted.length > 0) {
      await handleDeleteDataByOne('customer-company-fundings', documentKey)
    }
  }

  const handleSetCustomerCompanyFundingToSearch = (customerCompanyFundingToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1
    customerCompanyFundingToSearchRef.current = customerCompanyFundingToSearchData

    setErrors({})
    setCurrentListPage(1)
    setCustomerCompanyFundingToSearch(customerCompanyFundingToSearchData)

    if (customerCompanyFundingToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentCustomerCompanyFundingsDataState = currentCustomerCompanyFundingsDataRef.current ?? []
      const customerCompanyFundingsPerPage = currentCustomerCompanyFundingsDataState.slice(firstPageIndex, lastPageIndex)

      setCustomerCompanyFundingsList(customerCompanyFundingsPerPage)
      setSearchedCustomerCompanyFundings([])

      if (showError) setErrors({ customerCompanyFundingToSearch: t('searchField.errors.emptyField', { ns: 'payments-to-review-management' }) })
      return
    }

    const currentFullCustomerComanyFundingsData = [...currentCustomerCompanyFundingsDataRef.current]
    const filteredCustomerCompanyFundings = currentFullCustomerComanyFundingsData.filter((customerCompanyFundingData) => {
      const customerCompanyFundingCreatedAt = format(addHours(new Date(customerCompanyFundingData.createdAt), 5), 'dd/MM/yyyy')
      const customerCompanyFundingQuantity = String(Number(customerCompanyFundingData.fundingSaleQuantity) * Number(customerCompanyFundingData.fundingSaleRate))
      const customerCompanyDescription = handleIsValidMatch(customerCompanyFundingToSearchData.toLowerCase(), String(customerCompanyFundingData.description).toLowerCase())
      const customerCompanyFundingStatus =
        customerCompanyFundingData.status === 'processing' ? t('fundingStatus.processing') : customerCompanyFundingData.status === 'rejected' ? t('fundingStatus.rejected') : t('fundingStatus.approved')

      return (
        customerCompanyFundingQuantity.toLowerCase().includes(customerCompanyFundingToSearchData.toLowerCase()) ||
        customerCompanyFundingCreatedAt.toLowerCase().includes(customerCompanyFundingToSearchData.toLowerCase()) ||
        customerCompanyFundingStatus.toLowerCase().includes(customerCompanyFundingToSearchData.toLowerCase()) ||
        customerCompanyDescription
      )
    })

    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const customerCompanyFundingsPerPage = filteredCustomerCompanyFundings.slice(firstPageIndex, lastPageIndex)

    setCustomerCompanyFundingsList(customerCompanyFundingsPerPage)
    setSearchedCustomerCompanyFundings(filteredCustomerCompanyFundings)
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="customer-company-fundings-history-container">
      <div className="actions-bar">
        <div className="left-content">
          <div className="title">{customerCompanyFundingsList.length === 0 ? t('actionsBar.thereIsNoHistory') : t('actionsBar.fundingsHistory')}</div>
          {!loadingLocalData && customerCompanyFundingsList.length > 0 && (
            <button
              style={{ cursor: 'pointer' }}
              className="filter-button-container"
              onClick={() => setOpenFundingsHistoryReportModal(true)}
            >
              <img
                src={DownloadIcon}
                alt="download-icon"
              />
              <div className="filter-button-text">{t('actionsBar.downloadReport', { ns: 'customer-details-management' })}</div>
            </button>
          )}
        </div>
        <TextField
          variant="standard"
          sx={textFieldWhiteBg}
          label={t('searchField.placeholder')}
          value={customerCompanyFundingToSearch}
          error={!!errors.customerCompanyFundingToSearch}
          helperText={errors.customerCompanyFundingToSearch}
          className="search-input"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              handleSetCustomerCompanyFundingToSearch(customerCompanyFundingToSearch, true)
            }
          }}
          onChange={(event) => handleSetCustomerCompanyFundingToSearch(event.target.value, true)}
          onBlur={() => {
            if (customerCompanyFundingToSearch === '') setErrors({})
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  aria-label="toggle password visibility"
                  sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                >
                  <img
                    src={SearchIcon}
                    alt="search-icon"
                  />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </div>
      <div className="table-header-container">
        <div className="payment-list-item-data-left-container funding-list-item-left-container width-70-sm">
          <div className="table-header-text hide-sm-down left-content">{t('tableHeaders.date')}</div>
          <div
            style={{ justifyContent: 'start' }}
            className="table-header-text right-content padding-right-80-px justify-content-flex-start flex-direction-row-sm"
          >
            {t('tableHeaders.amount')}
          </div>
        </div>
        <div
          style={{ justifyContent: 'start' }}
          className="payment-list-item-data-right-container funding-list-item-right-container gap-10 width-30-sm"
        >
          <div className="table-header-text hide-sm-down left-content width-70">
            <div className="display-none-995">{t('tableHeaders.description')}</div>
          </div>
          <div
            style={{ justifyContent: 'start' }}
            className="table-header-text right-content last-table-header width-30"
          >
            {t('tableHeaders.actions')}
          </div>
        </div>
      </div>
      {loadingLocalData ? (
        <div className="users-management-users-list-container">
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
        </div>
      ) : (
        <>
          <div className="users-management-users-list-container">
            {customerCompanyFundingsList.map((customerCompanyFundingData: CustomerCompanyFunding) => {
              return (
                <CustomerCompanyFundingDataListItem
                  key={customerCompanyFundingData._id}
                  customerCompanyFundingData={customerCompanyFundingData}
                  handleOpenCustomerCompanyFundingDetailsModal={(customerCompanyFundingData: CustomerCompanyFunding) => {
                    setSelectedCustomerCompanyFunding(customerCompanyFundingData)
                    setOpenCustomerCompanyFundingDetailsModal(true)
                  }}
                />
              )
            })}
          </div>
          <Pagination
            pageSize={pageSize}
            currentPage={currentListPage}
            handlePaginationPageChange={(page: number) => {
              setTimeout(() => {
                setScrollToTop(true)
                setCurrentListPage(page)
                currentListPageRef.current = page
              }, 100)
            }}
            handleChangePageSize={(pageSizeToSet: string) => {
              setScrollToTop(true)
              setPageSize(Number(pageSizeToSet))
            }}
            totalCount={customerCompanyFundingToSearch === '' ? customerCompanyFundingsData.length : searchedCustomerCompanyFundings.length}
          />
        </>
      )}
      {openCustomerCompanyFundingDetailsModal && (
        <CustomerCompanyFundingDetailsModal
          selectedCustomerCompanyFunding={selectedCustomerCompanyFunding}
          handleOpenRevertCustomerCompanyFundingModal={() => setOpenRevertCustomerCompanyFundingModal(true)}
          handleOpenRejectCustomerCompanyFundingModal={() => setOpenRejectCustomerCompanyFundingModal(true)}
          handleSetModalOpenState={(openState: boolean) => setOpenCustomerCompanyFundingDetailsModal(openState)}
        />
      )}
      {openRejectCustomerCompanyFundingModal && (
        <RejectCustomerCompanyFundingModal
          selectedCustomerCompanyFunding={selectedCustomerCompanyFunding}
          handleCloseFundingAllocationModal={() => setOpenCustomerCompanyFundingDetailsModal(false)}
          handleSetModalOpenState={(openState: boolean) => setOpenRejectCustomerCompanyFundingModal(openState)}
        />
      )}
      {openRevertCustomerCompanyFundingModal && (
        <RevertCustomerCompanyFundingModal
          selectedCustomerCompanyFunding={selectedCustomerCompanyFunding}
          handleCloseFundingAllocationModal={() => setOpenCustomerCompanyFundingDetailsModal(false)}
          handleSetModalOpenState={(openState: boolean) => setOpenRevertCustomerCompanyFundingModal(openState)}
        />
      )}
      {openFundingsHistoryReportModal && (
        <CustomerCompanyFundingsHistoryReportModal
          customerCompanyId={String(customerCompanyId)}
          handleSetModalOpenState={(openState: boolean) => {
            setOpenFundingsHistoryReportModal(openState)
          }}
        />
      )}
    </div>
  )
}
